import { useEffect, useRef, useState } from "react";
import client from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2 } from "lucide-react";

const newSession = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now()));

// Google suggestions come without coordinates; fetch them only for the picked one.
export async function resolveAddress(r, session) {
  if (!r.placeId || r.lat != null) return r;
  const { data } = await client.get("/geo/place", { params: { id: r.placeId, session } });
  return data;
}

// Type-ahead UK address lookup via backend proxy.
// onSelect receives { address, postcode, lat, lng }.
export function AddressAutocomplete({ value, onChange, onSelect, placeholder = "Start typing an address…", testid = "address-autocomplete", className = "" }) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);
  const timer = useRef(null);
  const skipNext = useRef(false);
  const session = useRef(newSession());

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (skipNext.current) { skipNext.current = false; return; }
    const q = (value || "").trim();
    if (timer.current) clearTimeout(timer.current);
    if (q.length < 2) { setResults([]); setOpen(false); setLoading(false); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await client.get("/geo/autocomplete", { params: { q, session: session.current } });
        setResults(data.results || []);
        setOpen(true);
        setActive(-1);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 450);
    return () => timer.current && clearTimeout(timer.current);
  }, [value]);

  const pick = async (r) => {
    if (r.isPostcode) { onChange(r.address); return; }
    let full = r;
    try { full = await resolveAddress(r, session.current); } catch { full = r; }
    session.current = newSession();
    skipNext.current = true;
    onChange(full.address);
    onSelect && onSelect(full);
    setOpen(false);
    setResults([]);
  };

  const onKeyDown = (e) => {
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); pick(results[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <Input
          data-testid={testid}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          className={`pl-9 ${className}`}
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />}
      </div>
      {open && results.length > 0 && (
        <div data-testid={`${testid}-results`} className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          {results.map((r, i) => (
            <button
              type="button"
              key={i}
              data-testid={`${testid}-option-${i}`}
              onMouseDown={(e) => { e.preventDefault(); pick(r); }}
              onMouseEnter={() => setActive(i)}
              className={`flex w-full items-start gap-2 px-3 py-2 text-left text-sm ${active === i ? "bg-brand/10" : "hover:bg-slate-50"}`}
            >
              <MapPin className="h-3.5 w-3.5 mt-0.5 text-brand shrink-0" />
              <span className="text-slate-700 leading-snug">
                {r.address}
                {r.postcode && !(r.address || "").includes(r.postcode) && <span className="ml-1 font-medium text-slate-900">· {r.postcode}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
